import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import eventService from '../../../services/api/eventService';

/**
 * useEvents - Custom hook for managing calendar event data
 *
 * Features:
 * - Fetch events with date range and location filters
 * - Fetch locations for filter dropdown
 * - CRUD operations with optimistic updates
 * - Loading and error states
 * - Automatic refetch on params change
 * - Manual refetch capability
 *
 * @param {Object} params - Filter parameters
 * @param {string} params.startDate - ISO date string for range start
 * @param {string} params.endDate - ISO date string for range end
 * @param {string} [params.locationId] - Optional location filter
 */
const useEvents = (params = {}) => {
  // State
  const [events, setEvents] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs for lifecycle management
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const hasFetchedRef = useRef(false);

  // Memoize params to stable reference - only changes when values change
  const stableParams = useMemo(() => ({
    startDate: params.startDate,
    endDate: params.endDate,
    locationId: params.locationId
  }), [params.startDate, params.endDate, params.locationId]);

  // Stable param key for comparison
  const paramsKey = useMemo(() =>
    `${stableParams.startDate}-${stableParams.endDate}-${stableParams.locationId || ''}`,
    [stableParams]
  );

  /**
   * Fetch events from API
   * Handles abort for in-flight requests when params change
   */
  const fetchEvents = useCallback(async (fetchParams, options = {}) => {
    const { isRefetch = false } = options;

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      if (!isRefetch) {
        setError(null);
      }

      const response = await eventService.getEvents(fetchParams);

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        if (response.success) {
          setEvents(response.events || []);
          hasFetchedRef.current = true;
        } else {
          throw new Error(response.error || 'Failed to fetch events');
        }
      }
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') return;

      if (isMountedRef.current) {
        console.error('Error fetching events:', err);
        setError(err.message || 'Failed to load events');
        setEvents([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Fetch locations from API (one-time on mount)
   */
  const fetchLocations = useCallback(async () => {
    try {
      const response = await eventService.getLocations();

      if (isMountedRef.current && response.success) {
        setLocations(response.locations || []);
      }
    } catch (err) {
      // Locations are optional - log but don't set error
      console.error('Error fetching locations:', err);
    }
  }, []);

  // Fetch events when params change
  useEffect(() => {
    fetchEvents(stableParams);
  }, [paramsKey, fetchEvents, stableParams]);

  // Fetch locations once on mount
  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // Cancel any in-flight requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Manual refetch - forces a new fetch with current params
   */
  const refetch = useCallback(() => {
    fetchEvents(stableParams, { isRefetch: true });
    fetchLocations();
  }, [fetchEvents, fetchLocations, stableParams]);

  /**
   * Create event with immediate state update
   */
  const createEvent = useCallback(async (eventData) => {
    const response = await eventService.createEvent(eventData);

    if (response.success && response.event) {
      // Add permission flags — the creator is always the current user
      const eventWithFlags = {
        ...response.event,
        currentUserIsCreator: true
      };
      setEvents(prev => [...prev, eventWithFlags]);
      return eventWithFlags;
    }

    throw new Error(response.error || 'Failed to create event');
  }, []);

  /**
   * Update event with optimistic update and rollback on failure
   */
  const updateEvent = useCallback(async (eventId, updateData) => {
    let originalEvent;

    try {
      // Optimistic update
      setEvents(prev => {
        originalEvent = prev.find(e => e.id === eventId);
        return prev.map(event =>
          event.id === eventId ? { ...event, ...updateData } : event
        );
      });

      const response = await eventService.updateEvent(eventId, updateData);

      if (response.success && response.event) {
        // Merge server response with existing event data
        // IMPORTANT: Preserve permission flags (currentUserIsCreator, currentUserIsOrganizer,
        // currentUserIsAssignee) that are computed during getEvents but NOT returned by updateEvent
        setEvents(prev =>
          prev.map(event =>
            event.id === eventId ? {
              ...event,           // Keep existing data (including permission flags)
              ...response.event   // Overlay with server response (authoritative for updated fields)
            } : event
          )
        );
        return response.event;
      }

      throw new Error(response.error || 'Failed to update event');
    } catch (err) {
      // Rollback on error
      if (originalEvent) {
        setEvents(prev =>
          prev.map(event =>
            event.id === eventId ? originalEvent : event
          )
        );
      }
      console.error('Error updating event:', err);
      throw err;
    }
  }, []);

  /**
   * Delete event with optimistic update and rollback on failure
   */
  const deleteEvent = useCallback(async (eventId) => {
    let deletedEvent;

    try {
      // Optimistic update - remove from list
      setEvents(prev => {
        deletedEvent = prev.find(e => e.id === eventId);
        return prev.filter(event => event.id !== eventId);
      });

      const response = await eventService.deleteEvent(eventId);

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete event');
      }
    } catch (err) {
      // Rollback on error
      if (deletedEvent) {
        setEvents(prev => [...prev, deletedEvent]);
      }
      console.error('Error deleting event:', err);
      throw err;
    }
  }, []);

  /**
   * Change event status with optimistic update
   */
  const changeEventStatus = useCallback(async (eventId, status, reason = null) => {
    let originalEvent;

    try {
      // Optimistic update
      setEvents(prev => {
        originalEvent = prev.find(e => e.id === eventId);
        return prev.map(event =>
          event.id === eventId ? { ...event, status } : event
        );
      });

      const response = await eventService.changeStatus(eventId, status, reason);

      if (response.success && response.event) {
        // Merge response with existing event to preserve joined fields (customerName, etc.)
        setEvents(prev =>
          prev.map(event =>
            event.id === eventId ? { ...event, ...response.event } : event
          )
        );
        return response.event;
      }

      throw new Error(response.error || 'Failed to change event status');
    } catch (err) {
      // Rollback on error
      if (originalEvent) {
        setEvents(prev =>
          prev.map(event =>
            event.id === eventId ? originalEvent : event
          )
        );
      }
      console.error('Error changing event status:', err);
      throw err;
    }
  }, []);

  /**
   * Add assignee to event - refetches to get updated data
   */
  const addAssignee = useCallback(async (eventId, employeeId, options = {}) => {
    const response = await eventService.addAssignee(eventId, employeeId, options);

    if (response.success) {
      // Refetch to get updated assignees
      await fetchEvents(stableParams, { isRefetch: true });
      return response.assignee;
    }

    throw new Error(response.error || 'Failed to add assignee');
  }, [fetchEvents, stableParams]);

  /**
   * Remove assignee from event - refetches to get updated data
   */
  const removeAssignee = useCallback(async (eventId, employeeId) => {
    const response = await eventService.removeAssignee(eventId, employeeId);

    if (response.success) {
      // Refetch to get updated assignees
      await fetchEvents(stableParams, { isRefetch: true });
      return;
    }

    throw new Error(response.error || 'Failed to remove assignee');
  }, [fetchEvents, stableParams]);

  return {
    // Data
    events,
    locations,

    // State
    loading,
    error,

    // Actions
    refetch,
    createEvent,
    updateEvent,
    deleteEvent,
    changeEventStatus,
    addAssignee,
    removeAssignee
  };
};

export default useEvents;
